import React from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  ArrowTrendingUpIcon,
  BanknotesIcon,
  DocumentTextIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { Invoice, Client, Company } from "../types";
import { DashboardStats } from "./DashboardStats";
import { QuarterlyTaxStats } from "./QuarterlyTaxStats";
import { AutoBillingStatus } from "./AutoBillingStatus";

interface DashboardHomeProps {
  company: Company | null;
  invoices: Invoice[];
  clients: Client[];
  apiStats?: { expenseCount?: number; expenseTotal?: number; receivedInvoiceCount?: number };
  onNavigate: (tab: string) => void;
  onNewInvoice: () => void;
}

function buildCashflowSeries(invoices: Invoice[]) {
  const months: Record<string, number> = {};
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = d.toLocaleDateString("es-ES", { month: "short" });
    months[key] = 0;
  }
  invoices.forEach((inv) => {
    const d = new Date(inv.date);
    const key = d.toLocaleDateString("es-ES", { month: "short" });
    if (key in months) months[key] += Number(inv.total || 0);
  });
  return Object.entries(months).map(([name, total]) => ({ name, total }));
}

const STATUS_LABELS: Record<string, string> = {
  paid: "Pagada",
  sent: "Enviada",
  draft: "Borrador",
  overdue: "Vencida",
};

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    paid: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:ring-emerald-800",
    sent: "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:ring-blue-800",
    draft: "bg-slate-50 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900/20 dark:text-slate-400 dark:ring-slate-700",
    overdue: "bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-800",
  };
  return map[status] || map.draft;
};

export const DashboardHome: React.FC<DashboardHomeProps> = ({
  company,
  invoices,
  clients,
  apiStats,
  onNavigate,
  onNewInvoice,
}) => {
  const cashflow = buildCashflowSeries(invoices);
  const now = new Date();
  const yearlyRevenue = invoices
    .filter((i) => new Date(i.date).getFullYear() === now.getFullYear())
    .reduce((s, i) => s + Number(i.total || 0), 0);
  const monthlyRevenue = invoices
    .filter((i) => {
      const d = new Date(i.date);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    })
    .reduce((s, i) => s + Number(i.total || 0), 0);
  const paidCount = invoices.filter((i) => i.status === "paid").length;

  return (
    <div className="space-y-8 max-w-6xl">
      {company && (
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-[#8898aa] uppercase tracking-wider mb-1">Resumen financiero</p>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A2540] dark:text-[#e6edf3]">{company.name}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onNewInvoice}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#635BFF] text-white text-sm font-semibold px-4 py-2 hover:bg-[#4F46E5] shadow-sm transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Nueva factura
            </button>
            <button
              type="button"
              onClick={() => onNavigate("work-expenses")}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22] text-[#425466] dark:text-[#8b949e] text-sm font-medium px-4 py-2 hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] transition-colors"
            >
              <BanknotesIcon className="h-4 w-4" />
              Gastos
            </button>
          </div>
        </div>
      )}

      <DashboardStats invoices={invoices} clients={clients} apiStats={apiStats} premium />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Cashflow chart */}
        <div className="xl:col-span-2 rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] p-6 shadow-stripe">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-semibold text-[#0A2540] dark:text-[#e6edf3] flex items-center gap-2">
                <ArrowTrendingUpIcon className="h-4.5 w-4.5 h-[18px] w-[18px] text-[#635BFF]" />
                Flujo de caja
              </h2>
              <p className="text-xs text-[#8898aa] mt-0.5">Últimos 6 meses · facturación</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold tabular-nums text-[#0A2540] dark:text-[#e6edf3]">{yearlyRevenue.toFixed(0)} €</p>
              <p className="text-xs text-[#8898aa]">año {now.getFullYear()} · <span className="font-medium text-emerald-600 dark:text-emerald-400">{monthlyRevenue.toFixed(0)} € este mes</span></p>
            </div>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cashflow}>
                <defs>
                  <linearGradient id="cashGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#635BFF" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#635BFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8898aa' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#8898aa' }} axisLine={false} tickLine={false} width={48} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #E6E9F0",
                    background: "#fff",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    fontSize: 12,
                  }}
                  formatter={(v: number) => [`${v.toFixed(2)} €`, "Total"]}
                />
                <Area type="monotone" dataKey="total" stroke="#635BFF" fill="url(#cashGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick actions */}
        <div className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] p-6 shadow-stripe">
          <h2 className="text-sm font-semibold text-[#0A2540] dark:text-[#e6edf3] mb-4">Acciones rápidas</h2>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Facturas", tab: "invoices", icon: DocumentTextIcon },
              { label: "Clientes", tab: "clients", icon: PlusIcon },
              { label: "Importar", tab: "import", icon: ArrowTrendingUpIcon },
              { label: "Email", tab: "email", icon: DocumentTextIcon },
            ].map((a) => (
              <button
                key={a.tab}
                type="button"
                onClick={() => onNavigate(a.tab)}
                className="flex flex-col items-start gap-2 rounded-lg border border-[#E6E9F0] dark:border-[#30363d] p-3.5 hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] text-left transition-colors group"
              >
                <a.icon className="h-4.5 w-4.5 h-[18px] w-[18px] text-[#635BFF]" />
                <span className="text-xs font-medium text-[#425466] dark:text-[#8b949e] group-hover:text-[#0A2540] dark:group-hover:text-[#e6edf3] transition-colors">{a.label}</span>
              </button>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-[#E6E9F0] dark:border-[#21262d]">
            <p className="text-xs text-[#8898aa]">Facturas cobradas</p>
            <p className="text-2xl font-semibold tabular-nums text-[#0A2540] dark:text-[#e6edf3] mt-1">
              {paidCount}
              <span className="text-sm font-normal text-[#8898aa]"> / {invoices.length}</span>
            </p>
          </div>
        </div>
      </div>

      <QuarterlyTaxStats invoices={invoices} />

      <AutoBillingStatus onProcessBilling={() => alert("Procesando facturación automática…")} />

      {invoices.length > 0 && (
        <div className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] shadow-stripe overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E9F0] dark:border-[#21262d]">
            <h2 className="text-sm font-semibold text-[#0A2540] dark:text-[#e6edf3]">Facturas recientes</h2>
            <button
              type="button"
              onClick={() => onNavigate("invoices")}
              className="text-xs font-medium text-[#635BFF] hover:text-[#4F46E5] transition-colors"
            >
              Ver todas →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#E6E9F0] dark:border-[#21262d]">
                  <th className="px-6 py-3 text-xs font-medium text-[#8898aa] uppercase tracking-wider">Nº</th>
                  <th className="px-6 py-3 text-xs font-medium text-[#8898aa] uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-3 text-xs font-medium text-[#8898aa] uppercase tracking-wider">Importe</th>
                  <th className="px-6 py-3 text-xs font-medium text-[#8898aa] uppercase tracking-wider">Estado</th>
                </tr>
              </thead>
              <tbody>
                {invoices.slice(0, 8).map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-[#F6F9FC] dark:border-[#21262d]/50 hover:bg-[#F6F9FC] dark:hover:bg-[#21262d]/30 transition-colors"
                  >
                    <td className="px-6 py-3.5 font-medium tabular-nums text-[#0A2540] dark:text-[#e6edf3]">#{inv.number}</td>
                    <td className="px-6 py-3.5 text-[#425466] dark:text-[#8b949e]">{inv.client?.name}</td>
                    <td className="px-6 py-3.5 font-semibold tabular-nums text-[#0A2540] dark:text-[#e6edf3]">{Number(inv.total || 0).toFixed(2)} €</td>
                    <td className="px-6 py-3.5">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge(inv.status)}`}>
                        {STATUS_LABELS[inv.status] || inv.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
